import { useEffect, useState, useContext } from 'react';
import { UserContext } from './UserContext';
import { isAdminType, canManageUser, canonicalRole } from './utils/roleUtils.js';
import Sidebar from './Sidebar.js';
import { Card, Image, Row, Col, Container, Form, Button, Modal } from 'react-bootstrap';
import Breadcrumb from './Breadcrumb.js';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import UserAddGeneral from './UserAddGeneral';
import UserAddAbout from './UserAddAbout';
import UserAddResidence from './UserAddResidence';
import api from './api/axiosInstance'; // Make sure it attaches token
import NavTabs from './NavTabs';
import icSucess from './assets/img/ic-celebration-circle.svg';

const UserEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { user: actor } = useContext(UserContext);

    const actorRole = canonicalRole(actor?.role);
    const actorId = String(actor?._id || '');

    const [targetUserRole, setTargetUserRole] = useState('');

    // Form fields state
    const [errors, setErrors] = useState({});
    const [profileImage, setProfileImage] = useState(null);
    const [isImageDeleted, setIsImageDeleted] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [users, setUsers] = useState([]);
    const [activityStatus, setActivityStatus] = useState(true);
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [savedModalTitle, setSavedModalTitle] = useState('Update successful');
    const [savedModalMessage, setSavedModalMessage] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);

    const [createdDate, setCreatedDate] = useState('');

    const [generalData, setGeneralData] = useState({
        profileImage: '',
        firstName: '',
        familyName: '',
        gender: '',
        company: '',
        companyRole: '',
        activityStatus: false,
    })

    const [aboutData, setAboutData] = useState({
        email: '',
        birthDate: '',
        phone: '',
        company: '',
        companyRole: '',
    })

    const [residenceData, setResidenceData] = useState({
        nationality: '',
        country: '',
        city: '',
        zipCity: '',
        citizenships: [],
    })

    const userNavItems = [
        { to: '/user-edit/:id', eventKey: 'userInfo', label: 'About' },
        { to: '/user-edit-academics/:id', eventKey: 'academicsInfo', label: 'Academics' },
        { to: '/user-edit-work-preferences/:id', eventKey: 'work', label: 'Work Preferences' },
        { to: '/user-edit-access/:id', eventKey: 'access', label: 'Access & Permissions' },
        { to: '/user-edit-history/:id', eventKey: 'history', label: 'History of changes' },
    ];

    const normalizeId = (x) => {
        if (!x) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'object') {
            if (x._id) return normalizeId(x._id);
            if (x.$oid) return String(x.$oid);
            if (x.id) return normalizeId(x.id);
            if (typeof x.toHexString === 'function') return x.toHexString();
            if (typeof x.toString === 'function' && x.toString !== Object.prototype.toString) {
                const s = x.toString();
                if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
            }
        }
        return '';
    };

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res = await api.get('/api/companies');
                setCompanies(res.data);
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };

        fetchCompanies();
    }, []);

    

    const [tickets, setTickets] = useState([]);
    useEffect(() => {
        const fetchTickets = async () => {
            try {
                // ✅ goes through interceptor; token auto-attached
                const res = await api.get('/api/tickets'); // ✅
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }   
        };
        fetchTickets();
    }, []);

    useEffect(() => {
        // Fetch user data for editing
        const fetchUserData = async () => {
            try {
                const res = await api.get(`/api/users/${id}`);
                const currentUser = res.data;

                // setActivityStatus(companyData.activityStatus ?? false);

                if (!currentUser) {
                    alert('User not found.');
                    return;
                }

                setTargetUserRole(canonicalRole(currentUser.role || ''));
                
                setCreatedDate(currentUser.createdAt || '');

                // Set general data
                setGeneralData({
                    profileImage: currentUser.profileImage || '',
                    firstName: currentUser.firstName || '',
                    familyName: currentUser.familyName || '',
                    gender: currentUser.gender || '',
                    profileImagePreview: currentUser.profileImage || '',
                    company: normalizeId(currentUser.company),
                    companyRole: currentUser.companyRole || '',
                    activityStatus: currentUser.activityStatus || false,
                });

                // Set about data
                setAboutData({
                    email: currentUser.email || '',
                    birthDate: currentUser.birthDate ? currentUser.birthDate.slice(0, 10) : '',
                    phone: currentUser.phone || '',
                    company: normalizeId(currentUser.company),
                    companyRole: currentUser.companyRole || '',
                });

                // Set about data
                setResidenceData({
                    nationality: currentUser.nationality || currentUser.citizenships?.[0] || '',
                    country: currentUser.country || '',
                    city: currentUser.city || '',
                    zipCity: currentUser.zipCity?.toString() || '',
                    citizenships: Array.isArray(currentUser.citizenships)
                        ? currentUser.citizenships.slice(0, 2)
                        : [
                            currentUser.citizenship1 || currentUser.nationality || '',
                            currentUser.citizenship2 || '',
                        ].filter(Boolean),
                });

            } catch (error) {
                console.error('Error fetching user data:', error);
                alert('An error occurred while fetching user details.');
            }
        };

        fetchUserData();
    }, [id]);

    const isSelfEdit = actorId && actorId === String(id);

    const hideEditSameOrHigher =
        !isSelfEdit &&
        isAdminType(actorRole) &&
        isAdminType(targetUserRole) &&
        !canManageUser(actorRole, targetUserRole);

    const validateGeneralFields = () => {
        const newErrors = {};

        if (!generalData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!generalData.familyName.trim()) newErrors.familyName = 'Last name is required';
        if (!generalData.gender) newErrors.gender = 'Gender is required';
        

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateAboutFields = () => {
        const newErrors = {};

        if (!aboutData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(aboutData.email)) {
            newErrors.email = 'Invalid email format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateResidenceFields = () => {
        const newErrors = {};

        if (!(residenceData.citizenships?.[0] || residenceData.nationality || '').trim()) {
            newErrors.citizenships = 'Citizenship #1 is required';
        }
        if (!residenceData.country.trim()) newErrors.country = 'Country is required';
        if (!residenceData.city.trim()) newErrors.city = 'City is required';
        if (!String(residenceData.zipCity).trim()) newErrors.zipCity = 'Zip Code is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateGeneralFields()) return false; // ✅ Return false if validation fails

        try {
            const data = new FormData();

            data.append('firstName', generalData.firstName || '');
            data.append('familyName', generalData.familyName || '');
            data.append('gender', generalData.gender || '');

            if (profileImage) {
                data.append('profileImage', profileImage);
            }

            await api.put(`/api/users/${id}`, data);

            setSavedModalMessage('User General information updated successfully.');
            setShowSavedModal(true);
            return true; // ✅ Return true on success
        } catch (error) {
            console.error('Error updating general information:', error);
            setShowErrorMessage(true);
            return false; // ✅ Return false on failure
        }
    };

    const handleAboutChange = (e) => {
        const { name, value } = e.target;

        console.log('handleChange called with:', name, value);
        
        setAboutData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleAboutSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!validateAboutFields()) return false;
        
        const payload = {
            email: aboutData.email,
            birthDate: aboutData.birthDate,
            phone: aboutData.phone,
            company: aboutData.company,
            companyRole: aboutData.companyRole,
        };

        console.log('📤 Sending about data:', payload); // ✅ Log the payload

        try {
            await api.put(`/api/users/${id}/about`, payload);

            setSavedModalMessage('User About and contact information updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (error) {
            console.error('Error updating about information:', error);

            if (
                error.response?.status === 400 &&
                error.response.data?.msg === 'User already exists'
            ) {
                setErrors((prev) => ({ ...prev, email: 'This email is already in use' }));
            } else {
                alert('An error occurred while updating about information.');
            }

            return false;
        }
    };

    const handleResidenceChange = (e) => {
        const { name, value } = e.target;

        console.log('handleChange called with:', name, value);
        
        setResidenceData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleResidenceSubmit = async (e) => {
        if (e) e.preventDefault();
        
        if (!validateResidenceFields()) return false; // ✅ return false on validation failure

        try {
            await api.put(`/api/users/${id}/residence`, {
                nationality: residenceData.citizenships?.[0] || residenceData.nationality || '',
                country: residenceData.country,
                city: residenceData.city,
                zipCity: residenceData.zipCity,
                citizenships: residenceData.citizenships || [],
                citizenship1: residenceData.citizenships?.[0] || '',
                citizenship2: residenceData.citizenships?.[1] || '',
            });

            setSavedModalMessage('User Nationality & residence information updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (error) {
            console.error('Error updating residence information:', error);
            alert('Error updating residence information');
            return false;
        }
    }

    const handleGeneralChange = (e) => {
        const { name, value, files } = e.target;

        console.log('handleChange called with:', name, value);

        if (name === 'profileImage' && files && files.length > 0) {
            const file = files[0];
            const fileUrl = URL.createObjectURL(file);
            setProfileImage(file);
            setGeneralData((prevData) => ({
                ...prevData,
                profileImagePreview: fileUrl, // ✅ only preview, not actual file
            }));
            return;
        }

        setGeneralData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleAboutReset = (resetData) => {
        setAboutData(resetData);
        setErrors({});
    };

    const handleGeneralReset = (resetData) => {
        setGeneralData(resetData);
        setErrors({});
    };

    const handleResidenceReset = (resetData) => {
        setResidenceData(resetData);
        setErrors({});
    };

    const isActive = generalData.activityStatus;

    const handleDeactivate = async () => {
        try {
            const endpoint = isActive ? `/api/users/${id}/deactivate` : `/api/users/${id}/activate`;
            await api.put(endpoint);
            setShowDeactivateModal(false);
            setGeneralData((prev) => ({ ...prev, activityStatus: !isActive }));
        } catch (err) {
            console.error('Error toggling account status:', err);
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/api/users/${id}`);
            setShowDeleteModal(false);
            navigate('/onboarding-user-list');
        } catch (err) {
            console.error('Error deleting account:', err);
        }
    };

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleGeneralSubmit}>
                                                            <UserAddGeneral
                                                                formData={{ 
                                                                    ...generalData,
                                                                    company: aboutData.company,
                                                                    companyRole: aboutData.companyRole,
                                                                }}
                                                                profileImage={generalData.profileImagePreview}
                                                                handleChange={handleGeneralChange}
                                                                handleGeneralSubmit={handleGeneralSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                activityStatus={activityStatus}
                                                                handleReset={handleGeneralReset}
                                                                companies={companies}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={userNavItems} />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleAboutSubmit}>
                                                            <UserAddAbout
                                                                formData={aboutData}
                                                                handleChange={handleAboutChange}
                                                                handleAboutSubmit={handleAboutSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                companies={companies}
                                                                createdDate={createdDate}
                                                                handleReset={handleAboutReset}
                                                                hideEdit={hideEditSameOrHigher}
                                                                userRole={targetUserRole}
                                                            />
                                                        </Form>
                                                    </Col>
                                                    <Col>
                                                        <Form onSubmit={handleResidenceSubmit}>
                                                            <UserAddResidence
                                                                formData={residenceData}
                                                                handleChange={handleResidenceChange}
                                                                handleResidenceSubmit={handleResidenceSubmit}
                                                                errors={errors}
                                                                isEditPage={true}
                                                                handleReset={handleResidenceReset}
                                                                hideEdit={hideEditSameOrHigher}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Link to="/user-add" className="btn btn-outline-primary">
                                Add new user
                            </Link>
                            <Button variant="outline-secondary" onClick={() => setShowDeactivateModal(true)}>{isActive ? 'Deactivate account' : 'Activate account'}</Button>
                            <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete account</Button>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isActive ? 'Deactivate account' : 'Activate account'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{isActive
                        ? 'Are you sure you want to deactivate this account?'
                        : 'Are you sure you want to reactivate this account?'
                    }</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeactivateModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleDeactivate}>{isActive ? 'Deactivate' : 'Activate'}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete account</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to permanently delete this account? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDelete}>Delete account</Button>
                </Modal.Footer>
            </Modal>

            {/* Saved info Modal */}
            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">{savedModalTitle}</h2>
                    <p className="text-center">{savedModalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserEdit;
